'use client';

import React, { useState } from 'react';
import { Calendar, CheckCircle2, Clock, Loader2, Send } from 'lucide-react';

interface ServiceBookingFormProps {
  serviceId: string;
  serviceTitle: string;
  basePrice: number;
  isQuotationOnly: boolean;
}

export function ServiceBookingForm({
  serviceId,
  serviceTitle,
  basePrice,
  isQuotationOnly,
}: ServiceBookingFormProps) {
  const [loading, setLoading] = useState(false);
  const [successCode, setSuccessCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    city: 'Elbasan',
    addressLine: '',
    preferredDate: '',
    notes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/services/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, serviceId }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Dështoi regjistrimi i shërbimit.');
      }

      setSuccessCode(data.bookingNumber);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (successCode) {
    return (
      <div className="bg-white p-8 rounded-3xl border border-slate-200 text-center space-y-4 shadow-sm">
        <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-bold text-slate-900">Takimi u Regjistrua!</h3>
        <p className="text-xs text-slate-600">
          Kodi i Takimit Tuaj: <span className="font-mono font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded">{successCode}</span>
        </p>
        <p className="text-xs text-slate-500">
          Ekipi ynë do t'ju telefonojë për të konfirmuar orarin e saktë të mbërritjes së teknikut në adresë.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
      <div className="pb-4 border-b border-slate-100">
        <h2 className="text-lg font-bold text-slate-900">Rezervo Shërbimin</h2>
        <div className="text-xs text-slate-500 mt-1">
          Tarifa: <strong className="text-slate-900">{isQuotationOnly ? 'Me Preventiv' : `${basePrice.toLocaleString()} ALL`}</strong>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 mb-1">Emri dhe Mbiemri *</label>
          <input
            type="text"
            required
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
            placeholder="psh. Artan Basha"
            className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 mb-1">Telefoni *</label>
            <input
              type="tel"
              required
              value={form.customerPhone}
              onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
              placeholder="+355 69 ..."
              className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div>
            <label className="block font-semibold text-slate-700 mb-1">Email *</label>
            <input
              type="email"
              required
              value={form.customerEmail}
              onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
              placeholder="email@shembull.al"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 mb-1">Qyteti *</label>
            <select
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="w-full px-2 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
            >
              <option value="Elbasan">Elbasan</option>
              <option value="Tiranë">Tiranë</option>
              <option value="Durrës">Durrës</option>
              <option value="Fier">Fier</option>
              <option value="Vlorë">Vlorë</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="block font-semibold text-slate-700 mb-1">Adresa / Lagjja *</label>
            <input
              type="text"
              required
              value={form.addressLine}
              onChange={(e) => setForm({ ...form, addressLine: e.target.value })}
              placeholder="Rruga, Ndërtesa, Hyrja"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1">Data e Preferuar *</label>
          <input
            type="date"
            required
            value={form.preferredDate}
            onChange={(e) => setForm({ ...form, preferredDate: e.target.value })}
            className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1">Shënime ose Modeli i Pajisjes</label>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="psh. Daikin 12000 BTU, kati i tretë..."
            className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs shadow-lg shadow-sky-600/30 flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Duke konfirmuar...
            </>
          ) : (
            'Rezervo Takimin Teknik'
          )}
        </button>
      </form>
    </div>
  );
}
